import React from 'react';

const ACTIVE = '#2563EB';
const INACTIVE = '#99A0AE';
const BORDER = '#E1E4EA';

export interface TabItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ color: string }>;
  badge?: number;
}

interface TabBarProps {
  items: TabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  assistantOpen: boolean;
  onToggleAssistant: () => void;
}

export default function TabBar({
  items,
  activeId,
  onSelect,
  assistantOpen,
  onToggleAssistant,
}: TabBarProps): JSX.Element {
  return (
    <div
      style={{
        height: 46,
        flexShrink: 0,
        background: '#fff',
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
        boxSizing: 'border-box',
      }}
    >
      <button
        type="button"
        aria-label="STAC Assistant"
        aria-pressed={assistantOpen}
        onClick={onToggleAssistant}
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 8,
          border: `1px solid ${assistantOpen ? ACTIVE : BORDER}`,
          background: assistantOpen ? '#EFF4FF' : '#F8FAFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <AssistantTriggerIcon />
      </button>
      <div style={{ width: 1, alignSelf: 'stretch', background: BORDER, margin: '10px 12px' }} />
      <div style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
        {items.map((item, i) => {
          const active = item.id === activeId;
          const color = active ? ACTIVE : INACTIVE;
          const Icon = item.icon;
          return (
            <React.Fragment key={item.id}>
              {i === 1 && <div style={{ width: 1, background: BORDER, margin: '10px 4px' }} />}
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${active ? ACTIVE : 'transparent'}`,
                  cursor: 'pointer',
                }}
              >
                <Icon color={color} />
                <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color }}>
                  {item.label}
                </span>
                {!!item.badge && (
                  <span
                    style={{
                      background: '#EF4444',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '1px 6px',
                      fontSize: 9,
                      fontWeight: 700,
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function AssistantTriggerIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9.42334 6.13141H5.97386C4.19577 6.13141 2.75435 7.57283 2.75435 9.35092V14.4102H0.799644V9.35092C0.799644 6.49328 3.11622 4.1767 5.97386 4.1767H9.42334V6.13141Z"
        fill="#2563EB"
      />
      <path
        d="M15.083 9.86834C14.7973 12.4236 12.6298 14.4102 9.99825 14.4102H4.4216V12.4554H9.99825C11.5483 12.4554 12.8377 11.3402 13.1081 9.86834H15.083Z"
        fill="#2563EB"
      />
      <path
        d="M7.18118 9.29343C7.18118 9.86496 6.71786 10.3283 6.14633 10.3283C5.57481 10.3283 5.11149 9.86496 5.11149 9.29343C5.11149 8.7219 5.57481 8.25859 6.14633 8.25859C6.71786 8.25859 7.18118 8.7219 7.18118 9.29343Z"
        fill="#2563EB"
      />
      <path
        d="M10.8606 9.29343C10.8606 9.86496 10.3973 10.3283 9.82578 10.3283C9.25425 10.3283 8.79093 9.86496 8.79093 9.29343C8.79093 8.7219 9.25425 8.25859 9.82578 8.25859C10.3973 8.25859 10.8606 8.7219 10.8606 9.29343Z"
        fill="#2563EB"
      />
      <path
        d="M13.0624 3.64513C13.1073 3.57239 13.2131 3.57239 13.258 3.64513L14.1338 5.0629C14.1432 5.0781 14.156 5.0909 14.1712 5.1003L15.5889 5.97605C15.6617 6.02098 15.6617 6.12676 15.5889 6.1717L14.1712 7.04745C14.156 7.05684 14.1432 7.06964 14.1338 7.08484L13.258 8.50261C13.2131 8.57535 13.1073 8.57535 13.0624 8.50261L12.1866 7.08484C12.1772 7.06964 12.1644 7.05684 12.1492 7.04745L10.7315 6.1717C10.6587 6.12676 10.6587 6.02098 10.7315 5.97605L12.1492 5.1003C12.1644 5.0909 12.1772 5.0781 12.1866 5.0629L13.0624 3.64513Z"
        fill="#E97135"
      />
      <path
        d="M15.8228 7.54867C15.868 7.47767 15.9716 7.47767 16.0168 7.54867L16.4427 8.21787C16.4518 8.23207 16.4638 8.24411 16.478 8.25314L17.1472 8.67903C17.2182 8.72422 17.2182 8.82786 17.1472 8.87304L16.478 9.29893C16.4638 9.30797 16.4518 9.32001 16.4427 9.3342L16.0168 10.0034C15.9716 10.0744 15.868 10.0744 15.8228 10.0034L15.3969 9.3342C15.3879 9.32001 15.3759 9.30797 15.3617 9.29893L14.6925 8.87304C14.6215 8.82786 14.6215 8.72422 14.6925 8.67903L15.3617 8.25314C15.3759 8.24411 15.3879 8.23207 15.3969 8.21787L15.8228 7.54867Z"
        fill="#E97135"
      />
    </svg>
  );
}

export function NexusIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M2.84861 14.6062C4.31319 15.8896 6.23403 16.6667 8.33335 16.6667C10.4347 16.6667 12.3534 15.8896 13.8208 14.6062C15.1243 13.2785 10.8236 8.55833 8.33335 6.67775C5.84583 8.55833 1.54236 13.2785 2.84861 14.6062ZM10.5986 4.60208C12.3347 6.65833 15.7958 11.7639 14.818 13.5687C15.9736 12.1389 16.6667 10.3202 16.6667 8.33608C16.6667 6.01667 15.7188 3.91806 14.1875 2.40833C14.1875 2.40833 14.1688 2.39306 14.1305 2.37917C14.0868 2.36389 14.025 2.34792 13.9354 2.34792C13.5243 2.34792 12.5569 2.64931 10.5986 4.60208ZM2.5375 2.37917C2.49792 2.39306 2.48055 2.40764 2.47777 2.40833C0.947917 3.91806 0 6.01667 0 8.33608C0 10.3181 0.69305 12.1368 1.84792 13.5673C0.875 11.7583 4.33333 6.65694 6.07013 4.60208C4.1118 2.64861 3.14236 2.34861 2.73263 2.34861C2.64167 2.34861 2.57777 2.36319 2.5375 2.38056V2.37917ZM8.33335 2.46597C8.33335 2.46597 6.28819 1.26944 4.69097 1.2125C4.06388 1.18958 3.68125 1.41736 3.63472 1.44792C5.1243 0.448608 6.7076 0 8.32218 0H8.33335C9.95418 0 11.5313 0.448608 13.0319 1.44792C12.9847 1.41597 12.6048 1.18958 11.9763 1.2125C10.3792 1.26944 8.33335 2.46181 8.33335 2.46181V2.46597Z"
        fill={color}
      />
    </svg>
  );
}

export function ClassifyIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M7.5 4.16667V12.5H5V4.16667H7.5ZM4.16667 2.5C3.70643 2.5 3.33333 2.8731 3.33333 3.33333V13.3333C3.33333 13.7936 3.70643 14.1667 4.16667 14.1667H8.33333C8.79358 14.1667 9.16667 13.7936 9.16667 13.3333V3.33333C9.16667 2.8731 8.79358 2.5 8.33333 2.5H4.16667ZM12.5 7.5V12.5H15V7.5H12.5ZM10.8333 6.66667C10.8333 6.20643 11.2064 5.83333 11.6667 5.83333H15.8333C16.2936 5.83333 16.6667 6.20643 16.6667 6.66667V13.3333C16.6667 13.7936 16.2936 14.1667 15.8333 14.1667H11.6667C11.2064 14.1667 10.8333 13.7936 10.8333 13.3333V6.66667ZM17.5 15.8333H2.5V17.5H17.5V15.8333Z"
        fill={color}
      />
    </svg>
  );
}

export function ReturnsIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M10.0041 18.3356C5.40171 18.3356 1.67075 14.6047 1.67075 10.0023C1.67075 5.39992 5.40171 1.66896 10.0041 1.66896C14.6064 1.66896 18.3374 5.39992 18.3374 10.0023C18.3374 14.6047 14.6064 18.3356 10.0041 18.3356ZM10.0041 16.6689C13.686 16.6689 16.6708 13.6842 16.6708 10.0023C16.6708 6.32039 13.686 3.33563 10.0041 3.33563C6.32218 3.33563 3.33741 6.32039 3.33741 10.0023C3.33741 13.6842 6.32218 16.6689 10.0041 16.6689ZM7.08741 11.6689H11.6708C11.9008 11.6689 12.0874 11.4824 12.0874 11.2523C12.0874 11.0222 11.9008 10.8356 11.6708 10.8356H8.33743C7.18682 10.8356 6.25408 9.90292 6.25408 8.75225C6.25408 7.60169 7.18682 6.66896 8.33743 6.66896H9.17076V5.00229H10.8374V6.66896H12.9208V8.33559H8.33743C8.1073 8.33559 7.92075 8.52217 7.92075 8.75225C7.92075 8.98242 8.1073 9.16892 8.33743 9.16892H11.6708C12.8213 9.16892 13.7541 10.1017 13.7541 11.2523C13.7541 12.4029 12.8213 13.3356 11.6708 13.3356H10.8374V15.0023H9.17076V13.3356H7.08741V11.6689Z"
        fill={color}
      />
    </svg>
  );
}
